import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconEdit, IconDelete } from '../icons';
import { Product, formatPrice, formatTaxRate } from '../utils/productUtils';
import { useProducts } from '../hooks/useProducts';
import { useProductForm } from '../hooks/useProductForm';
import { useProductImport } from '../hooks/useProductImport';
import { ProductSearchHeader } from '../components/products/ProductSearchHeader';
import { EditProductModal } from '../components/products/EditProductModal';
import { ImportProductModal } from '../components/products/ImportProductModal';
import DataTable, { Column } from '../components/common/DataTable';
import { RowAction } from '../components/common/RowAction';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useIsReadOnly } from '../hooks/useIsReadOnly';

const ProductManagementPage: React.FC = () => {
  const { t } = useTranslation('product');
  
  // Hilfsfunktion zum Übersetzen von Fehlermeldungen aus Hooks
  const translateMessage = (message: string): string => {
    if (!message) return '';
    
    if (message.startsWith('loadError:')) {
      const error = message.replace('loadError:', '');
      return error === 'unknownError' ? t('messages.loadError') : `${t('messages.loadError')}: ${error}`;
    }
    if (message.startsWith('deleteError:')) {
      const error = message.replace('deleteError:', '');
      return error === 'unknownError' ? t('messages.deleteError') : `${t('messages.deleteError')}: ${error}`;
    }
    if (message.startsWith('saveError:')) {
      const error = message.replace('saveError:', '');
      return error === 'unknownError' ? t('messages.saveError') : `${t('messages.saveError')}: ${error}`;
    }
    if (message.startsWith('importError:')) {
      const error = message.replace('importError:', '');
      if (error === 'noData') return t('messages.importError') + ': ' + t('import.noDataError', 'Keine Daten zum Importieren vorhanden');
      if (error === 'noValidProducts') return t('messages.importError') + ': ' + t('import.noValidProductsError', 'Keine gültigen Produkte in den Import-Daten gefunden');
      return error === 'unknownError' ? t('messages.importError') : `${t('messages.importError')}: ${error}`;
    }
    if (message.startsWith('importSuccess:')) {
      const parts = message.replace('importSuccess:', '').split(':');
      const [successCount, newCount, updatedCount] = parts;
      return t('messages.importSuccessDetails', { successCount, newCount, updatedCount });
    }
    if (message === 'deleteSuccess') {
      return t('messages.deleteSuccess');
    }
    if (message.startsWith('saveSuccess:')) {
      const name = message.replace('saveSuccess:', '');
      return `${name} ${t('messages.saveSuccess')}.`;
    }
    if (message.startsWith('updateSuccess:')) {
      const name = message.replace('updateSuccess:', '');
      return `${name} ${t('messages.updateSuccess')}.`;
    }
    
    return message;
  };
  
  // Verwaltung des UI-Zustands für Modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  
  // Verwende den useProducts-Hook für Produktverwaltung
  // M-E: Sortierung übernimmt die DataTable via Column.sortValue/initialSort —
  // requestSort/getSortIndicator (Hook-Rückgabe) werden hier nicht mehr verdrahtet.
  const {
    loading,
    error,
    setError,
    searchTerm,
    setSearchTerm,
    filteredAndSortedProducts,
    handleDeleteProduct,
    updateProductInList,
  } = useProducts();

  // M11b-3: advisor (read-only) sieht keine Bearbeiten/Löschen-Aktionen.
  const isReadOnly = useIsReadOnly();
  
  // Verwende den useProductForm-Hook für Formularlogik
  const {
    formData,
    isPriceGross,
    isProcessing: isFormProcessing,
    editingProduct,
    loadProductForEdit,
    handleFormChange,
    togglePriceMode,
    saveForm,
    resetForm
  } = useProductForm(
    // onSuccess-Callback
    (savedProduct) => {
      // `editingProduct` unterscheidet Anlegen von Bearbeiten. Beim Bearbeiten
      // gilt patch-if-present: enthaelt eine nebenlaeufig eintreffende
      // Ladeantwort das Produkt nicht mehr, hat es ein anderes Geraet
      // geloescht — es darf dann nicht wiederauferstehen.
      updateProductInList(savedProduct, editingProduct ? 'update' : 'create');
      setIsEditModalOpen(false);
      setError(editingProduct ? `updateSuccess:${savedProduct.name}` : `saveSuccess:${savedProduct.name}`);
    },
    // onError-Callback
    (errorMessage) => {
      setError(errorMessage); // Pass raw error message
    }
  );
  
  // Verwende den useProductImport-Hook für Import-Funktionalität
  const {
    importData,
    fileName,
    isProcessing: isImportProcessing,
    useFileUpload,
    handleFileChange,
    handleTextChange,
    toggleInputMode,
    executeImport
  } = useProductImport(
    // onSuccess-Callback
    (message) => {
      setError(message); // Pass raw message
      setIsImportModalOpen(false);
    },
    // onError-Callback
    (errorMessage) => {
      setError(errorMessage); // Pass raw error message
    },
    // onFinish-Callback
    () => {
      setIsImportModalOpen(false);
    }
  );
  
  // Handler zum Öffnen des Bearbeitungsmodals
  const handleEdit = (product: Product) => {
    loadProductForEdit(product);
    setIsEditModalOpen(true);
  };
  
  // Handler zum Erstellen eines neuen Produkts
  const handleCreateProduct = () => {
    resetForm(); // Sicherstellen, dass das Formular zurückgesetzt ist
    setIsEditModalOpen(true); // Modal öffnen
  };
  
  // Handler zum Schließen der Modals
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    resetForm();
  };
  
  // Handler für die Produktsuche
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  /**
   * Lösch-Bestätigung (Regression-Fix 2026-08-08).
   *
   * Die M-E-Migration auf `DataTable` hat `ProductTable.tsx`/`ProductTableRow.tsx`
   * ersetzt und dabei den Bestätigungsdialog verloren: `RowAction` "Löschen" rief
   * `handleDeleteWithMessage` direkt auf — ein Klick, unwiderruflich gelöscht,
   * ohne Rückfrage. Aufgefallen ist es erst, als die E2E-Suite überhaupt lief
   * (`e2e/products.spec.ts` erwartet seit jeher `getByRole('dialog')`).
   *
   * Dass es ein Verlust und keine Design-Entscheidung war, belegen die
   * verwaisten Übersetzungen `messages.deleteTitle` / `messages.deleteConfirm`
   * (mit `{{name}}`-Platzhalter) in allen sechs Sprachen — sie werden seit der
   * Migration von keiner Stelle mehr gerendert. Kunden/Webhooks nutzen dieselbe
   * `ConfirmationDialog`-Konvention; Produkte fallen jetzt wieder darunter.
   */
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const closeDeleteDialog = () => setProductToDelete(null);

  // Handler für Produktlöschung mit übersetzter Erfolgsmeldung
  const handleDeleteWithMessage = async (productId: string) => {
    const success = await handleDeleteProduct(productId);
    if (success) {
      setError(t('messages.deleteSuccess'));
    }
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    const productId = productToDelete.id;
    closeDeleteDialog();
    await handleDeleteWithMessage(productId);
  };

  // Spalten der Produkttabelle (M-E: DataTable ersetzt ProductTable.tsx/ProductTableRow.tsx —
  // die vormals duplizierte requestSort/getSortIndicator-Mechanik entfällt zugunsten von
  // Column.sortValue + DataTable-initialSort; Spalten-Diät lt. modules-ux-density-v2.md §M-E).
  const productColumns: Column<Product>[] = [
    {
      id: 'name',
      header: t('table.headers.product'),
      accessor: (product) => product.name,
      sortValue: (product) => product.name || '',
    },
    {
      id: 'description',
      header: t('table.headers.description'),
      accessor: (product) => product.description || '-',
      sortValue: (product) => product.description || '',
      truncate: 'max-w-xs',
      hideBelow: 'md',
    },
    {
      id: 'netPrice',
      header: t('table.headers.netPrice'),
      accessor: (product) => formatPrice(product.netPrice),
      sortValue: (product) => product.netPrice ?? -Infinity,
      numeric: true,
    },
    {
      id: 'taxRate',
      header: t('table.headers.vat'),
      accessor: (product) => formatTaxRate(product.taxRate),
      sortValue: (product) => product.taxRate ?? -Infinity,
      numeric: true,
    },
    {
      id: 'sellerArticleId',
      header: t('table.headers.articleNumber'),
      accessor: (product) => product.sellerArticleId || '-',
      sortValue: (product) => product.sellerArticleId || '',
      hideBelow: 'lg',
    },
    // Aktionen — M11b-3: für advisor (read-only) ausgeblendet
    {
      id: 'actions',
      header: t('table.headers.actions'),
      align: 'right',
      sticky: 'right',
      accessor: (product) =>
        !isReadOnly ? (
          <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
            <RowAction label={t('edit')} icon={<IconEdit />} variant="accent" onClick={() => handleEdit(product)} />
            <RowAction
              label={t('delete')}
              icon={<IconDelete />}
              variant="danger"
              onClick={() => setProductToDelete(product)}
            />
          </div>
        ) : null,
    },
  ];

  return (
    <div className="bg-surface text-primary min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Kopfzeile mit Titel, Suchfeld und Import-Button */}
          <ProductSearchHeader
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onImportClick={() => setIsImportModalOpen(true)}
            onCreateClick={handleCreateProduct}
          />
          
          {/* Fehlermeldung oder Erfolgsmeldung */}
          {error && (
            <div className={`${
              error.startsWith('saveSuccess:') || 
              error.startsWith('updateSuccess:') || 
              error === 'deleteSuccess' || 
              error.startsWith('importSuccess:') 
                ? 'bg-[var(--tone-success-bg)] border-[var(--tone-success-border)] text-[var(--tone-success-fg)]'
                : 'bg-[var(--tone-danger-bg)] border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)]'
            } border px-4 py-3 rounded-md mb-4`}>
              {translateMessage(error)}
            </div>
          )}
          
          {/* Produkttabelle */}
          {loading ? (
            <div className="bg-surface-raised rounded-xl overflow-hidden shadow-lg border border-hairline flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : (
            <DataTable<Product>
              columns={productColumns}
              rows={filteredAndSortedProducts}
              rowKey={(product) => product.id}
              density="compact"
              initialSort={{ id: 'name', dir: 'asc' }}
              empty={
                <div className="text-center text-secondary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p className="text-lg">{t('noProductsFound')}</p>
                  <p className="text-muted mt-2">{t('automaticallyAdded')}</p>
                </div>
              }
            />
          )}
        </div>
      </div>

      {/* Produkt-Bearbeitungsmodal */}
      <EditProductModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        formData={formData}
        isPriceGross={isPriceGross}
        isProcessing={isFormProcessing}
        isEditing={!!editingProduct}
        error={error}
        onSubmit={saveForm}
        onChange={handleFormChange}
        onTogglePriceMode={togglePriceMode}
      />

      {/* Import-Modal */}
      <ImportProductModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        importData={importData}
        fileName={fileName}
        useFileUpload={useFileUpload}
        isProcessing={isImportProcessing}
        error={error}
        onImport={executeImport}
        onFileChange={handleFileChange}
        onTextChange={handleTextChange}
        onToggleInputMode={toggleInputMode}
      />

      {/* Lösch-Bestätigungsdialog (s. Kommentar an `productToDelete`) */}
      <ConfirmationDialog
        isOpen={!!productToDelete}
        title={t('messages.deleteTitle')}
        message={t('messages.deleteConfirm', { name: productToDelete?.name || '' })}
        options={[
          {
            label: t('cancel'),
            action: closeDeleteDialog,
            variant: 'secondary',
          },
          {
            label: t('delete'),
            action: confirmDelete,
            variant: 'danger',
          },
        ]}
        onClose={closeDeleteDialog}
      />
    </div>
  );
};

export default ProductManagementPage; 